import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '~/pages/base.component';
import { HttpService } from '~/core/services/http.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { FormatterService } from '~/core/services/formatter.service';
import { WellsApiService } from '~/services/wells-api.service';
import { TabNavigationService } from '~/services/tab-navigation.service';
import { NotificationService } from '~/services/notification.service';
import { UnitConversionService } from '~/services/unit-conversion.service';


@Component({
    selector: 'app-well-prepare',
    templateUrl: './well-prepare-component.html',
    styleUrls: ['./well-prepare-component.scss'],
    standalone: false
})
export class WellPrepareComponent extends BaseComponent implements OnInit, OnDestroy {
    public wellName: string = '';
    public bu: string = '';
    public stagesCompleted: string[] = [];
    public schemas: any[] = [];
    public processedSchemas: any[] = [];
    public loading: boolean = false;
    public loadingSchemas: boolean = false;
    
    // Schema ordering configuration for ABU
    private readonly SCHEMA_ORDER_ABU = [
        'Well Header',
        'Wellbore',
        'Wellbore Section',
        'Geological Horizon',
        'Tubing',
        'Tubing Components',
        'Rod',
        'Rod Components',
        'Casing',
        'Casing Components',
        'Cement',
        'Cement Stage',
        'Perforation',
        'Other in Hole'
    ];
    
    // Mapping from API schema names to display names for ABU
    private readonly SCHEMA_NAME_MAP_ABU: { [key: string]: string } = {
        'cas': 'Casing',
        'casing': 'Casing',
        'cascomp': 'Casing Components',
        'casing_components': 'Casing Components',
        'casingcomponents': 'Casing Components',
        'cement': 'Cement',
        'cementstage': 'Cement Stage',
        'cement_stage': 'Cement Stage',
        'perforation': 'Perforation',
        'tubing': 'Tubing',
        'tubingcomp': 'Tubing Components',
        'tubing_components': 'Tubing Components',
        'tubingcomponents': 'Tubing Components',
        'rod': 'Rod',
        'rodcomp': 'Rod Components',
        'rod_components': 'Rod Components',
        'rodcomponents': 'Rod Components',
        'wellheader': 'Well Header',
        'well_header': 'Well Header',
        'wellbore': 'Wellbore',
        'wellboresz': 'Wellbore Section',
        'wellbore_sz': 'Wellbore Section',
        'wellboresection': 'Wellbore Section',
        'wellboreformation': 'Geological Horizon',
        'wellbore_formation': 'Geological Horizon',
        'otherinhole': 'Other in Hole',
        'other_in_hole': 'Other in Hole',
    };
    
    // Schema ordering configuration for RBU
    private readonly SCHEMA_ORDER_RBU = [
        'Well Header',
        'Wellbore',
        'Wellbore Section',
        'Geological Horizon',
        'Jobs',
        'Casing',
        'Casing Components',
        'Cement',
        'Cement Stage',
        'Perforation',
        'Other in Hole'
    ];
    
    // Mapping from API schema names to display names for RBU
    private readonly SCHEMA_NAME_MAP_RBU: { [key: string]: string } = {
        'cas': 'Casing',
        'casing': 'Casing',
        'cascomp': 'Casing Components',
        'casing_components': 'Casing Components',
        'casingcomponents': 'Casing Components',
        'cement': 'Cement',
        'cementstage': 'Cement Stage',
        'cement_stage': 'Cement Stage',
        'perforation': 'Perforation',
        'job': 'Jobs',
        'jobs': 'Jobs',
        'wellheader': 'Well Header',
        'well_header': 'Well Header',
        'wellbore': 'Wellbore',
        'wellboresz': 'Wellbore Section',
        'wellbore_sz': 'Wellbore Section',
        'wellboresection': 'Wellbore Section',
        'wellboreformation': 'Geological Horizon',
        'wellbore_formation': 'Geological Horizon',
        'otherinhole': 'Other in Hole',
        'other_in_hole': 'Other in Hole',
    };

    // Field mappings: schema-specific mappings for display labels to API keys
    private readonly FIELD_MAPPINGS: Record<string, Record<string, string>> = {
        'wellheader': {
            'Well Name': 'wellname',
            'API Number': 'wellida',
            'Operator Name': 'operator',
            'Well Status': 'currentwellstatus1',
            'Well Sub Status': 'currentwellstatus2',
            'Original Spud Date': 'dttmspud',
            'Country': 'country',
            'State/Province': 'stateprov',
            'Latitude': 'latitude',
            'Longitude': 'longitude',
            'Comments': 'com',
            'Original RKB': 'elvorigkb',
            'Ground Elevation': 'elvground',
            'idwell': 'idwell'
        },
        'wellbore': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Wellbore Name': 'des',
            'Wellbore UWI': 'wellboreida',
            'Start Depth': 'depthstart'
        },
        'wellboresz': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Proposed/Actual': 'proposedoractual',
            'Holesize': 'sz',
            'Top Depth': 'depthtop',
            'Bottom Depth': 'depthbtm',
            'Start Date & Time': 'dttmstart',
            'End Date & Time': 'dttmend',
            'Section Description': 'des'
        },
        'wellboreformation': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Formation Name': 'formname',
            'Geologic Age': 'geologicage',
            'Final Top MD': 'depthfinaltop',
            'Final Bottom MD': 'depthfinalbtm'
        },
        'job': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Job Category': 'wvtyp',
            'Primary Job Type': 'jobtyp',
            'Actual Start Date': 'dttmstart',
            'Actual End Date': 'dttmend',
            'Target Depth': 'targetdepth'
        },
        'cas': {
            'idwell': 'idwell',
            'Proposed/Actual': 'proposedoractual',
            'Item Description': 'des',
            'Set Depth': 'depthbtm',
            'Run Date': 'dttmrun',
            'idrecwellbore': 'idrecwellbore',
            'Well Name': 'wellname'
        },
        'cascomp': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Item Description': 'des',
            'Component Length': 'length',
            'Component OD': 'szodnom',
            'Component ID': 'szidnom',
            'Weight per Length': 'wtperlength',
            'Grade': 'grade',
            'Component Type': 'compsubtyp'
        },
        'cement': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Cement Description': 'des',
            'Proposed/Actual': 'proposedoractual',
            'Cement Type': 'cementtyp',
            'Cement Start Date Time': 'dttmstart',
            'IDRecWellBore': 'idrecwellbore',
            'Comments': 'com'
        },
        'cementstage': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Bottom Depth': 'depthbtm',
            'Stage Number': 'stagenum',
            'Stage Description': 'des',
            'Comments': 'com',
            'Top Depth': 'depthtop',
            'Measurement Method': 'measmethod',
            'Stage Start Date Time': 'dttmstartpump'
        },
        'perforation': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Perforation Top Depth': 'depthtop',
            'Proposed/Actual': 'proposedoractual',
            'Perforation Date Time': 'dttm',
            'Perforation Type': 'typ',
            'Perforation Bottom Depth': 'depthbtm',
            'IDRecWellBore': 'idrecwellbore'
        },
        'otherinhole': {
            'idwell': 'idwell',
            'Description': 'des',
            'Proposed/Actual': 'proposedoractual',
            'Nominal OD': 'szodnom',
            'Component Sub Type': 'compsubtyp',
            'Top Depth': 'depthtop',
            'Bottom Depth': 'depthbtm',
            'Run Date Time': 'dttmrun',
            'idrecwellbore': 'idrecwellbore'
        },
        'tubing': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Item Description': 'des',
            'Set Depth': 'depthbtm',
            'Run Date': 'dttmrun',
            'idrecwellbore': 'idrecwellbore'
        },
        'tubingcomp': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Item Description': 'des',
            'Component Length': 'length',
            'Component OD': 'szodnom',
            'Component ID': 'szidnom',
            'Weight per Length': 'wtperlength',
            'Grade': 'grade',
            'Component Type': 'compsubtyp'
        },
        'rod': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Item Description': 'des',
            'Set Depth': 'depthbtm',
            'Run Date': 'dttmrun',
            'idrecwellbore': 'idrecwellbore'
        },
        'rodcomp': {
            'idwell': 'idwell',
            'Well Name': 'wellname',
            'Item Description': 'des',
            'Component Length': 'length',
            'Component OD': 'szodnom',
            'Component ID': 'szidnom',
            'Weight per Length': 'wtperlength',
            'Grade': 'grade',
            'Component Type': 'compsubtyp'
        }
    };

    private destroy$ = new Subject<void>();
    
    // Prepare Mode Properties
    public prepareInProgress: boolean = false;
    public prepareComplete: boolean = false;
    public prepareData: Map<string, any> = new Map();
    public prepareSchemaStatuses: Map<string, 'pending' | 'in-progress' | 'completed' | 'error'> = new Map();
    public prepareExpandedSchemas: Set<string> = new Set();
    public prepareProgress: number = 0;
    
    // Execute Mode Properties
    public executeInProgress: boolean = false;
    public executeComplete: boolean = false;
    public executeResult: Map<string, any> = new Map();
    public executeSchemaStatuses: Map<string, 'pending' | 'in-progress' | 'completed' | 'error'> = new Map();
    public executeSubStatus: Map<string, 'prepare' | 'execute'> = new Map();
    public executeExpandedSchemas: Set<string> = new Set();
    public executeProgress: number = 0;
    
    error: string = '';
    notificationType: 'success' | 'error' | 'info' = 'info';

    constructor(
        private httpService: HttpService,
        private appSettingsService: AppSettingsService,
        private formatterService: FormatterService,
        private route: ActivatedRoute,
        private router: Router,
        private tabNavigationService: TabNavigationService,
        private wellsApiService: WellsApiService,
        private notificationService: NotificationService,
        public unitConversionService: UnitConversionService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        console.log('[WellPrepareComponent] ngOnInit - Component initialized');
        await super.ngOnInit();
        
        this.bu = sessionStorage.getItem('selectedBU') || 'ABU';
        this.stagesCompleted = this.tabNavigationService.getStagesCompleted();
        if (!this.stagesCompleted || !Array.isArray(this.stagesCompleted)) {
            this.stagesCompleted = [];
        }

        // Get well name from route params
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            if (params['wellName']) {
                this.wellName = params['wellName'];
                this.bu = sessionStorage.getItem('selectedBU') || 'ABU';
                this.loadSchemas();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadSchemas(): void {
        this.loadingSchemas = true;
        this.wellsApiService.getSchemaList(this.wellName, this.bu).subscribe({
            next: (response: any) => {
                this.schemas = Array.isArray(response) ? response : (response.schemas || []);
                this.processAndOrderSchemas();
                this.loadingSchemas = false;
            },
            error: (err: any) => {
                console.error('[loadSchemas] Error loading schemas:', err);
                this.loadingSchemas = false;
                this.showNotification('Failed to load schemas: ' + (err.error?.detail || err.message || 'Unknown error'), 'error');
            }
        });
    }

    private processAndOrderSchemas(): void {
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const SCHEMA_ORDER = selectedBU === 'RBU' ? this.SCHEMA_ORDER_RBU : this.SCHEMA_ORDER_ABU;
        const SCHEMA_NAME_MAP = selectedBU === 'RBU' ? this.SCHEMA_NAME_MAP_RBU : this.SCHEMA_NAME_MAP_ABU;
        
        const normalizedApiSchemas = this.schemas.map(schema => {
            const apiName = typeof schema === 'string' ? schema : (schema.schema_type || schema.name || schema.type || schema.id || '');
            const normalizedApiName = apiName.toLowerCase().replace(/[\s_]+/g, '');
            const displayName = SCHEMA_NAME_MAP[normalizedApiName] || this.formatSchemaName(apiName);
            return { apiName, displayName };
        });
        
        const availableDisplayNames = new Set<string>();
        
        for (const apiSchema of this.schemas) {
            const apiName = typeof apiSchema === 'string' ? apiSchema : (apiSchema.schema_type || apiSchema.name || apiSchema.type || apiSchema.id || '');
            const normalizedApiName = apiName.toLowerCase().replace(/[\s_]+/g, '');
            const displayName = SCHEMA_NAME_MAP[normalizedApiName] || this.formatSchemaName(apiName);
            availableDisplayNames.add(displayName);
        }
        
        const orderedSchemas = SCHEMA_ORDER.map(displayName => {
            const schema = normalizedApiSchemas.find(s => s.displayName === displayName);
            return schema;
        }).filter(s => s !== undefined);
        
        this.processedSchemas = orderedSchemas;
    }

    private formatSchemaName(name: string): string {
        return name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    }

    /**
     * Manual trigger to start the prepare flow - called when user clicks button
     */
    public startPrepareFlowManual(): void {
        if (this.processedSchemas.length === 0) {
            this.showNotification('No schemas available to prepare', 'error');
            return;
        }
        this.startPrepareFlow();
    }
    private startPrepareFlow(): void {
        this.prepareInProgress = true;
        this.prepareComplete = false;
        this.prepareProgress = 0;
        this.prepareData.clear();
        this.prepareSchemaStatuses.clear();
        
        this.processedSchemas.forEach(schema => {
            this.prepareSchemaStatuses.set(schema.displayName, 'pending');
        });

        const enabledSchemas = this.processedSchemas;
        this.prepareSchemasSequentially(enabledSchemas, 0);
    }

    private prepareSchemasSequentially(schemas: any[], currentIndex: number): void {
        if (currentIndex >= schemas.length) {
            this.prepareInProgress = false;
            this.prepareComplete = true;
            console.log('[prepareSchemasSequentially] All schemas prepared');
            return;
        }

        const schema = schemas[currentIndex];
        const enabledCount = this.processedSchemas.length;

        this.prepareProgress = Math.round(((currentIndex + 1) / enabledCount) * 100);
        this.prepareSchemaStatuses.set(schema.displayName, 'in-progress');

        console.log(`[prepareSchemasSequentially] Preparing schema ${currentIndex + 1}/${enabledCount}: ${schema.displayName}`);

        // Get idrec from localStorage if available (for RBU)
        const Idrecitem = localStorage.getItem('selectedIdRec') || undefined;
        // console.log(Idrecitem);
        const idrec = Idrecitem ? JSON.parse(Idrecitem).idrec : undefined;
        console.log(`[prepareSchemasSequentially] Schema: ${schema.apiName}, idrec: ${idrec}`);

        // Use prepareSchemaChanges API for prepare flow (correct endpoint)
        this.wellsApiService.prepareSchemaChanges(schema.apiName, this.wellName, this.bu, idrec).subscribe({
            next: (data: any) => {
                this.prepareSchemaStatuses.set(schema.displayName, 'completed');
                
                // Store the data - add schema_name to each operation for detection
                if (data.operations && Array.isArray(data.operations)) {
                    data.operations.forEach((op: any) => {
                        if (!op.schema_name && !op.metadata?.schema_name) {
                            if (op.metadata) {
                                op.metadata.schema_name = schema.displayName;
                            } else {
                                op.metadata = { schema_name: schema.displayName };
                            }
                        }
                    });
                }
                this.prepareData.set(schema.displayName, data);
                
                // Automatically expand the first 3 schemas
                if (currentIndex < 3) {
                    this.prepareExpandedSchemas.add(schema.displayName);
                }
                
                setTimeout(() => {
                    this.prepareSchemasSequentially(schemas, currentIndex + 1);
                }, 300);
            },
            error: (err: any) => {
                console.error(`[prepareSchemasSequentially] Error preparing ${schema.displayName}:`, err);
                this.prepareSchemaStatuses.set(schema.displayName, 'error');
                
                setTimeout(() => {
                    this.prepareSchemasSequentially(schemas, currentIndex + 1);
                }, 300);
            }
        });
    }

    public getPrepareStatus(schemaName: string): 'pending' | 'in-progress' | 'completed' | 'error' {
        return this.prepareSchemaStatuses.get(schemaName) || 'pending';
    }

    public getExecuteStatus(schemaName: string): 'pending' | 'in-progress' | 'completed' | 'error' {
        return this.executeSchemaStatuses.get(schemaName) || 'pending';
    }

    public getPrepareDataForSchema(schemaName: string): any {
        return this.prepareData.get(schemaName);
    }

    public getExecuteDataForSchema(schemaName: string): any {
        return this.executeResult.get(schemaName);
    }

    public togglePrepareSchemaExpansion(schemaName: string): void {
        if (this.prepareExpandedSchemas.has(schemaName)) {
            this.prepareExpandedSchemas.delete(schemaName);
        } else {
            this.prepareExpandedSchemas.add(schemaName);
        }
    }

    public isPrepareSchemaExpanded(schemaName: string): boolean {
        return this.prepareExpandedSchemas.has(schemaName);
    }

    public toggleExecuteSchemaExpansion(schemaName: string): void {
        if (this.executeExpandedSchemas.has(schemaName)) {
            this.executeExpandedSchemas.delete(schemaName);
        } else {
            this.executeExpandedSchemas.add(schemaName);
        }
    }

    public isExecuteSchemaExpanded(schemaName: string): boolean {
        return this.executeExpandedSchemas.has(schemaName);
    }

    public navigateBackToList(): void {
        this.router.navigate(['/wells']);
    }

    private showNotification(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
        this.error = message;
        this.notificationType = type;
        
        if (type === 'success' || type === 'info') {
            setTimeout(() => {
                if (this.error === message) {
                    this.error = '';
                }
            }, 5000);
        }
    }
    /**
     * Ingest flow: prepare + execute each schema sequentially (schema-by-schema)
     */
    public startIngestFlowManual(): void {
        if (this.processedSchemas.length === 0) {
            this.showNotification('No schemas available to ingest', 'error');
            return;
        }
        this.startIngestFlow();
    }

    private startIngestFlow(): void {
        this.executeInProgress = true;
        this.executeComplete = false;
        this.executeProgress = 0;
        this.executeSchemaStatuses.clear();
        this.executeSubStatus.clear();
        this.executeResult.clear();
        this.prepareData.clear();

        this.processedSchemas.forEach(schema => {
            this.executeSchemaStatuses.set(schema.displayName, 'pending');
        });

        this.ingestSchemasSequentially(this.processedSchemas, 0);
    }

    private ingestSchemasSequentially(schemas: any[], currentIndex: number): void {
        if (currentIndex >= schemas.length) {
            this.executeInProgress = false;
            this.executeComplete = true;
            console.log('[ingestSchemasSequentially] All schemas ingested');
            return;
        }

        const schema = schemas[currentIndex];
        const totalCount = this.processedSchemas.length;

        this.executeProgress = Math.round((currentIndex / totalCount) * 100);
        this.executeSchemaStatuses.set(schema.displayName, 'in-progress');
        this.executeSubStatus.set(schema.displayName, 'prepare');

        const idrecItem = localStorage.getItem('selectedIdRec') || undefined;
        const idrec = idrecItem ? JSON.parse(idrecItem).idrec : undefined;

        console.log(`[ingestSchemasSequentially] Preparing schema ${currentIndex + 1}/${totalCount}: ${schema.displayName}`);

        // Step 1: Prepare
        this.wellsApiService.prepareSchemaChanges(schema.apiName, this.wellName, this.bu, idrec).subscribe({
            next: (prepareData: any) => {
                console.log(`[ingestSchemasSequentially] Prepare complete for ${schema.displayName}`);

                if (prepareData.operations && Array.isArray(prepareData.operations)) {
                    prepareData.operations.forEach((op: any) => {
                        if (!op.metadata) op.metadata = {};
                        if (!op.metadata.schema_name) op.metadata.schema_name = schema.displayName;
                    });
                }
                this.prepareData.set(schema.displayName, prepareData);
                this.executeSubStatus.set(schema.displayName, 'execute');

                // Step 2: Execute using prepare response
                this.wellsApiService.executeSchemaChanges(schema.apiName, prepareData).subscribe({
                    next: (executeData: any) => {
                        console.log(`[ingestSchemasSequentially] Execute complete for ${schema.displayName}`);
                        this.executeSchemaStatuses.set(schema.displayName, 'completed');

                        const ops = executeData.results || executeData.operations;
                        if (ops && Array.isArray(ops)) {
                            ops.forEach((op: any) => {
                                if (!op.metadata) op.metadata = {};
                                if (!op.metadata.schema_name) op.metadata.schema_name = schema.displayName;
                            });
                        }
                        this.executeResult.set(schema.displayName, executeData);
                        this.executeProgress = Math.round(((currentIndex + 1) / totalCount) * 100);

                        setTimeout(() => {
                            this.ingestSchemasSequentially(schemas, currentIndex + 1);
                        }, 300);
                    },
                    error: (err: any) => {
                        console.error(`[ingestSchemasSequentially] Execute error for ${schema.displayName}:`, err);
                        this.executeSchemaStatuses.set(schema.displayName, 'error');
                        setTimeout(() => {
                            this.ingestSchemasSequentially(schemas, currentIndex + 1);
                        }, 300);
                    }
                });
            },
            error: (err: any) => {
                console.error(`[ingestSchemasSequentially] Prepare error for ${schema.displayName}:`, err);
                this.executeSchemaStatuses.set(schema.displayName, 'error');
                setTimeout(() => {
                    this.ingestSchemasSequentially(schemas, currentIndex + 1);
                }, 300);
            }
        });
    }

    public startExecuteFlowManual(): void {
        if (this.processedSchemas.length === 0) {
            this.showNotification('No schemas available to execute', 'error');
            return;
        }
        this.startExecuteFlow();
    }
    private startExecuteFlow(): void {
        this.executeInProgress = true;
        this.executeComplete = false;
        this.executeProgress = 0;
        this.executeSchemaStatuses.clear();
        this.executeResult.clear();
        
        this.processedSchemas.forEach(schema => {
            this.executeSchemaStatuses.set(schema.displayName, 'pending');
        });

        const enabledSchemas = this.processedSchemas;
        this.executeSchemasSequentially(enabledSchemas, 0);
    }

    private executeSchemasSequentially(schemas: any[], currentIndex: number): void {
        if (currentIndex >= schemas.length) {
            this.executeInProgress = false;
            this.executeComplete = true;
            console.log('[executeSchemasSequentially] All schemas executed');
            
            return;
        }

        const schema = schemas[currentIndex];
        const enabledCount = this.processedSchemas.length;

        this.executeProgress = Math.round(((currentIndex + 1) / enabledCount) * 100);
        this.executeSchemaStatuses.set(schema.displayName, 'in-progress');

        console.log(`[executeSchemasSequentially] Executing schema ${currentIndex + 1}/${enabledCount}: ${schema.displayName}`);

        // Get the prepared data from prepareData map
        const preparedPayload = this.prepareData.get(schema.displayName);
        
        if (!preparedPayload) {
            console.warn(`[executeSchemasSequentially] No prepared data found for schema: ${schema.displayName}`);
            this.executeSchemaStatuses.set(schema.displayName, 'error');
            
            setTimeout(() => {
                this.executeSchemasSequentially(schemas, currentIndex + 1);
            }, 300);
            return;
        }

        // Use executeSchemaChanges API for execute flow with prepared payload
        this.wellsApiService.executeSchemaChanges(schema.apiName, preparedPayload).subscribe({
            next: (data: any) => {
                console.log(`[executeSchemasSequentially] Schema ${schema.displayName} executed successfully:`, data);
                this.executeSchemaStatuses.set(schema.displayName, 'completed');
                
                // Ensure schema_name is set in metadata for each operation
                const operationsArray = data.results || data.operations;
                if (operationsArray && Array.isArray(operationsArray)) {
                    operationsArray.forEach((op: any) => {
                        if (!op.schema_name && !op.metadata?.schema_name) {
                            if (op.metadata) {
                                op.metadata.schema_name = schema.displayName;
                            } else {
                                op.metadata = { schema_name: schema.displayName };
                            }
                        }
                    });
                }
                
                // Store results in executeResult map for display
                this.executeResult.set(schema.displayName, data);
                
                console.log(`[executeSchemasSequentially] Completed: ${schema.displayName}`);
                
                setTimeout(() => {
                    this.executeSchemasSequentially(schemas, currentIndex + 1);
                }, 300);
            },
            error: (err: any) => {
                console.error(`[executeSchemasSequentially] Error executing ${schema.displayName}:`, err);
                this.executeSchemaStatuses.set(schema.displayName, 'error');
                
                setTimeout(() => {
                    this.executeSchemasSequentially(schemas, currentIndex + 1);
                }, 300);
            }
        });
    }
    /**
     * Format field names to human-readable format
     * Converts API field names like "dttmend" to "End Date"
     */
    public formatFieldName(fieldName: string): string {
        // Mapping from API field names to display names
        const fieldNameMap: { [key: string]: string } = {
            'dttmend': 'End Date',
            'dttmstart': 'Start Date',
            'depthbtm': 'Bottom Depth',
            'depthtop': 'Top Depth',
            'sz': 'Size',
            'phase_number': 'Phase Number',
            'hole_size': 'Hole Size',
            'des': 'Description',
            'com': 'Comment',
            'idrec': 'Record ID',
            'idrecparent': 'Parent Record ID',
            'idwell': 'Well ID',
            'wellname': 'Well Name',
            'api': 'API Number',
            'api_number': 'API Number',
            'bu': 'Business Unit',
            'status': 'Status',
            'message': 'Message',
            'formation': 'Formation',
            'md': 'MD',
            'tvd': 'TVD',
            'grade': 'Grade',
            'wt': 'Weight',
            'od': 'OD',
            'id': 'ID',
            'type': 'Type',
            'manufacturer': 'Manufacturer'
        };
        
        const lowerFieldName = fieldName.toLowerCase();
        if (fieldNameMap[lowerFieldName]) {
            return fieldNameMap[lowerFieldName];
        }
        
        // Fallback: Convert snake_case/camelCase to Title Case
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .split(' ')
            .filter(word => word.length > 0)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Format values for display
     * Handles special formatting for dates and other field types
     */
    public formatValue(key: string, value: any): string {
        if (value === null || value === undefined) {
            return '-';
        }
        
        const dateFields = ['dttmend', 'dttmstart', 'dttmrun', 'date', 'datetime'];
        const lowerKey = key.toLowerCase();
        
        if (dateFields.some(df => lowerKey.includes(df))) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toISOString().split('T')[0];
                }
            } catch (e) {
                return String(value);
            }
        }
        
        return String(value);
    }

    /**
     * Get all content keys from schema data, excluding internal fields and non-primitive values
     */
    public getContentKeys(operation: any): string[] {
        if (!operation) {
            return [];
        }
        
        // Get the content object - either operation.content or operation itself
        const data = operation.content || operation;
        
        if (!data || typeof data !== 'object') {
            return [];
        }
        
        // Filter out internal fields, metadata, and complex objects
        return Object.keys(data).filter(key => {
            // Skip internal/system fields
            if (key === 'idrec' || key === 'idrecparent' || key === 'idwell' ||
                key === 'action' || key === 'metadata' || key === 'ui_keys' || 
                key === 'converted_values' || key === 'fields_to_update' ||
                key.startsWith('sys') || key.startsWith('_')) {
                return false;
            }
            
            const value = data[key];
            
            // Skip arrays and complex objects (but allow null/undefined)
            if (value !== null && value !== undefined && typeof value === 'object') {
                // Skip arrays and nested objects
                return false;
            }
            
            return true;
        });
    }

    /**
     * Check if a key is a unit variant key
     * Unit variant keys are fields that have unit conversion alternatives
     */
    public isUnitVariantKey(key: string): boolean {
        // Check if the key ends with unit variants like _ft, _m, _inch, etc.
        const unitPattern = /_(ft|m|inch|mm|cm|kg_per_m|lb_per_ft|ppg|api|sg)$/i;
        return unitPattern.test(key);
    }

    /**
     * Get operations array for a schema
     * Handles both array and single object responses
     */
    public getPrepareOperations(schemaName: string): any[] {
        const data = this.prepareData.get(schemaName);
        if (!data) {
            return [];
        }
        
        // If data has an operations array (standard response structure), return it
        if (data.operations && Array.isArray(data.operations)) {
            return data.operations;
        }
        
        // If data is already an array, return it
        if (Array.isArray(data)) {
            return data;
        }
        
        // If data is a single object, wrap it in an array
        if (typeof data === 'object') {
            return [data];
        }
        
        return [];
    }

    /**
     * Get operations array for execute schema
     * Handles both array and single object responses
     */
    public getExecuteOperations(schemaName: string): any[] {
        const data = this.executeResult.get(schemaName);
        if (!data) {
            return [];
        }
        
        // If data has a results array (execute response structure), return it
        if (data.results && Array.isArray(data.results)) {
            return data.results;
        }
        
        // If data has an execution_results array (alternative execute response structure), return it
        if (data.execution_results && Array.isArray(data.execution_results)) {
            return data.execution_results;
        }
        
        // If data has an operations array (standard response structure), return it
        if (data.operations && Array.isArray(data.operations)) {
            return data.operations;
        }
        
        // If data is already an array, return it
        if (Array.isArray(data)) {
            return data;
        }
        
        // If data is a single object, wrap it in an array
        if (typeof data === 'object') {
            return [data];
        }
        
        return [];
    }

    /**
     * Get action label for display
     */
    public getActionLabel(action: string): string {
        switch(action) {
            case 'PUT':
                return 'Update';
            case 'POST':
                return 'Create';
            case 'DELETE':
                return 'Delete';
            default:
                return action;
        }
    }

    /**
     * Get fields section title based on action
     */
    public getFieldsSectionTitle(action: string): string {
        switch(action) {
            case 'PUT':
                return 'Fields Updated';
            case 'POST':
                return 'Fields Created';
            case 'DELETE':
                return 'Fields';
            default:
                return 'Fields Updated';
        }
    }

    /**
     * Get fields to update from operation
     * Prioritizes fields_to_update array, falls back to content keys
     */
    public getFieldsToUpdate(operation: any): string[] {
        if (!operation) {
            return [];
        }
        
        // If fields_to_update exists, use it
        if (operation.fields_to_update && Array.isArray(operation.fields_to_update)) {
            return operation.fields_to_update;
        }
        
        // Fallback to content keys
        if (operation.content) {
            return this.getContentKeys(operation.content);
        }
        
        // Last fallback: all keys from operation excluding metadata
        return Object.keys(operation).filter(key => 
            key !== 'action' && 
            key !== 'fields_to_update' && 
            key !== 'ui_keys' && 
            key !== 'converted_values' &&
            key !== 'content'
        );
    }
    /**
     * Get UI label for an operation/result based on ui_keys array
     * Custom formatting based on schema type:
     * - Well Header: Show only Wellname value (ignore API Number)
     * - Geological Horizon: Show Surface/Formation + MD with label prefix (e.g., "WYODAK UPPER, MD: 178.918")
     * - Casing Component & others: Show as key-value pairs (Label: value, ...)
     * Falls back to Record N if ui_keys not available
     */
    public getOperationLabel(operation: any, index: number): string {
        if (!operation) return `Record ${index + 1}`;
        
        // Check for ui_keys array in the operation or result object
        const uiKeys = operation.ui_keys || operation.metadata?.ui_keys;
        
        if (uiKeys && Array.isArray(uiKeys) && uiKeys.length > 0) {
            // Try to detect schema from various sources
            const schemaName = operation.schema_name || operation.metadata?.schema_name || '';
            const normalizedSchema = schemaName.toLowerCase().replace(/[\s_]/g, '');
            
            // Well Header: Show only Wellname value
            if (normalizedSchema.includes('wellheader')) {
                const wellnameItem = uiKeys.find(item => 
                    item.label?.toLowerCase() === 'wellname'
                );
                if (wellnameItem) {
                    return String(wellnameItem.value);
                }
            }
            
            // Geological Horizon: Format as "Surface, MD: value" or "Formation, MD: value"
            if (normalizedSchema.includes('geological') || normalizedSchema.includes('formation') || normalizedSchema.includes('horizon')) {
                // Look for Surface or Formation
                const surfaceItem = uiKeys.find(item => 
                    item.label?.toLowerCase() === 'surface' || item.label?.toLowerCase() === 'formation'
                );
                const mdItem = uiKeys.find(item => 
                    item.label?.toLowerCase() === 'md'
                );
                
                const parts = [];
                if (surfaceItem) {
                    parts.push(String(surfaceItem.value));
                }
                if (mdItem) {
                    parts.push(`MD: ${mdItem.value}`);
                }
                
                if (parts.length > 0) {
                    return parts.join(', ');
                }
            }
            
            // Casing Component and other schemas: Show as key-value pairs
            // Format: "Label: value, Label2: value2"
            const keyValuePairs = uiKeys
                .filter(item => item.label && item.value !== null && item.value !== undefined)
                .map(item => `${item.label}: ${item.value}`);
            
            if (keyValuePairs.length > 0) {
                return keyValuePairs.join(', ');
            }
        }
        
        // Fallback to Record N if no ui_keys
        return `Record ${index + 1}`;
    }

    /**
     * Get display label for a field from FIELD_MAPPINGS
     */
    public getDisplayLabel(apiKey: string): string {
        // Search through all schema mappings to find the display label for this API key
        for (const schemaName in this.FIELD_MAPPINGS) {
            const schemaMapping = this.FIELD_MAPPINGS[schemaName];
            for (const [displayLabel, contentKey] of Object.entries(schemaMapping)) {
                if (contentKey === apiKey) {
                    return displayLabel;
                }
            }
        }
        
        // Fallback: format the key nicely
        return apiKey
            .replace(/_/g, ' ')
            .split(' ')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Get display fields from metadata mapped to display labels
     */
    public getDisplayFieldsFromMetadata(operation: any): string[] {
        if (!operation || !operation.metadata) {
            return [];
        }

        const fields = operation.metadata.fields_to_update || operation.metadata.fields_created || [];
        return fields.map((apiKey: string) => this.getDisplayLabel(apiKey));
    }

    /**
     * Count completed schemas in a status map
     */
    public getCompletedCount(statusMap: Map<string, string>): number {
        let count = 0;
        statusMap.forEach(status => {
            if (status === 'completed') {
                count++;
            }
        });
        return count;
    }

    /**
     * Get value for a field from operation content with proper key mapping
     * Handles accessing values using either direct field name or mapped content keys
     */
    public getFieldValue(operation: any, field: string): any {
        if (!operation || !field) return null;
        
        // If operation.content doesn't exist, return null
        if (!operation.content) return null;
        
        // FIRST: Try direct access with the field name (if it's already a content key)
        if (operation.content[field] !== undefined && operation.content[field] !== null) {
            return operation.content[field];
        }
        
        // SECOND: Try mapping the field name to a content key
        const contentKey = this.mapFieldNameToContentKey(field, operation.content);
        if (contentKey && operation.content[contentKey] !== undefined && operation.content[contentKey] !== null) {
            return operation.content[contentKey];
        }
        
        // THIRD: Try case-insensitive matching
        const lowerField = field.toLowerCase();
        for (const key of Object.keys(operation.content)) {
            if (key.toLowerCase() === lowerField) {
                return operation.content[key];
            }
        }
        
        return null;
    }

    /**
     * Map display field names to content keys
     * Searches through all schema-specific mappings in FIELD_MAPPINGS constant
     */
    private mapFieldNameToContentKey(fieldDisplayName: string, content: any): string | null {
        if (!content || !fieldDisplayName) return null;
        
        // STRATEGY 1: Direct lookup in FIELD_MAPPINGS constant (search all schemas)
        for (const schemaName in this.FIELD_MAPPINGS) {
            const schemaMapping = this.FIELD_MAPPINGS[schemaName];
            const contentKey = schemaMapping[fieldDisplayName];
            if (contentKey && content.hasOwnProperty(contentKey)) {
                return contentKey;
            }
        }
        
        // STRATEGY 2: Try case-insensitive match in content
        const normalizedField = fieldDisplayName.toLowerCase().replace(/[\s_&-]/g, '');
        for (const key of Object.keys(content)) {
            const normalizedKey = key.toLowerCase().replace(/[\s_&-]/g, '');
            if (normalizedKey === normalizedField) {
                return key;
            }
        }
        
        // STRATEGY 3: Try partial match
        for (const key of Object.keys(content)) {
            const normalizedKey = key.toLowerCase().replace(/[\s_&-]/g, '');
            if (normalizedKey.includes(normalizedField) || normalizedField.includes(normalizedKey)) {
                return key;
            }
        }
        
        return null;
    }

    /**
     * Set selected unit for a field in an operation
     */
    public setSelectedUnit(unit: string, operationId: string, fieldName?: string): void {
        const field = fieldName || 'global';
        this.unitConversionService.setSelectedUnit(operationId, field, unit);
    }

    /**
     * Get selected unit for a field in an operation
     */
    public getSelectedUnit(operationId: string, fieldName?: string, operation?: any): string {
        const field = fieldName || 'global';
        const record = operation?.metadata?.middleware_record || operation?.metadata?.middleware_item || operation;
        return this.unitConversionService.getSelectedUnit(operationId, field, record);
    }

    /**
     * Check if a field has unit variants
     */
    public hasUnitVariants(key: string, dataSource?: any): boolean {
        if (!key || !dataSource) return false;
        const record = dataSource?.metadata?.middleware_record || dataSource?.metadata?.middleware_item || dataSource;
        return this.unitConversionService.hasUnitVariants(key, record);
    }

    /**
     * Get available units for a field
     */
    public getAvailableUnits(fieldName: string, dataSource?: any): string[] {
        if (!fieldName || !dataSource) return [];
        const record = dataSource?.metadata?.middleware_record || dataSource?.metadata?.middleware_item || dataSource;
        return this.unitConversionService.getAvailableUnits(fieldName, record);
    }

    /**
     * Get display label for a unit
     */
    public getUnitDisplayLabel(unit: string): string {
        return this.unitConversionService.getUnitDisplayLabel(unit);
    }

    /**
     * Get the value for a field from operation content with unit conversion support
     * Returns converted value with unit label if field has unit variants
     */
    public getValueForOperationField(operation: any, field: string, operationId: string): any {
        if (!operation || !field || !operationId) {
            return null;
        }

        // Get the record which contains converted_values
        const record = operation.metadata?.middleware_record || operation.metadata?.middleware_item || operation;
        
        // FIRST: Check if this field has unit variants in converted_values
        if (this.unitConversionService.hasUnitVariants(field, record)) {
            const unitValue = this.unitConversionService.getValueForUnit(operationId, field, record);
            const selectedUnit = this.unitConversionService.getSelectedUnit(operationId, field, record);
            const unitLabel = this.unitConversionService.getUnitDisplayLabel(selectedUnit);
            
            if (unitValue !== undefined && unitValue !== null) {
                return `${unitValue} ${unitLabel}`;
            }
        }
        
        // SECOND: Try direct mapping to get value from content
        const contentKey = this.mapFieldNameToContentKey(field, operation.content);
        if (contentKey && operation.content && operation.content[contentKey] !== undefined) {
            return operation.content[contentKey];
        }
        
        // THIRD: Try direct field name match in content (case-insensitive)
        if (operation.content) {
            for (const key of Object.keys(operation.content)) {
                if (key.toLowerCase() === field.toLowerCase()) {
                    return operation.content[key];
                }
            }
        }
        
        return null;
    }
}


